"use client";

import { useEffect } from "react";
import { trackMarketingCtaClicked } from "./analytics";

/**
 * Destinations that signal conversion intent. Anything else (nav, docs,
 * social) stays covered by pageviews and autocapture.
 */
const CTA_PATHS = new Set(["/signup", "/book-a-demo", "/contact", "/login"]);

/**
 * Resolves a click target to CTA event properties, or null when the click
 * was not on a link to a conversion destination.
 */
export function resolveCtaClick(target: unknown): { cta: string; href: string } | null {
  if (!(target instanceof Element)) return null;
  const anchor = target.closest("a[href]");
  if (!anchor) return null;
  const href = anchor.getAttribute("href") ?? "";
  const path = href.split(/[?#]/)[0];
  if (!CTA_PATHS.has(path)) return null;
  return { cta: (anchor.textContent ?? "").trim().slice(0, 80), href: path };
}

/**
 * Delegated click listener for the marketing layout: fires "Marketing CTA
 * Clicked" for every link to a conversion destination, no matter which page
 * or component renders it. One listener instead of instrumenting dozens of
 * hero and CTA components by hand, and new CTAs are covered automatically.
 */
export function MarketingCtaTracking() {
  useEffect(() => {
    function onClick(event: MouseEvent) {
      const cta = resolveCtaClick(event.target);
      if (!cta) return;
      trackMarketingCtaClicked({ ...cta, location: window.location.pathname });
    }
    // Capture phase so the event records before Next.js Link navigation.
    document.addEventListener("click", onClick, { capture: true, passive: true });
    return () => document.removeEventListener("click", onClick, { capture: true });
  }, []);

  return null;
}
